import FileSaver from 'file-saver';
import { gzip } from 'pako/lib/deflate';
import { getFileType } from './filename';
import { uint8ArrayToBase64 } from './uint8ArrayToBase64';
/** vyuzije se pri prevodu relativnich URL na absolutni */
const anchor = document.createElement('a');
const imgToBase64 = async (img) => {
    const imgUrl = img.src;
    if (imgUrl.startsWith('data:'))
        return;
    try {
        const response = await fetch(imgUrl, {
            credentials: 'same-origin',
            headers: {
                Accept: 'image/avif,image/webp,image/svg+xml,image/*,*/*;q=0.8',
            },
        });
        const buffer = await response.arrayBuffer();
        let mimeType = '';
        if (/\.jpe?g$/i.test(imgUrl)) {
            mimeType = 'image/jpeg';
        }
        else if (/\.png$/i.test(imgUrl)) {
            mimeType = 'image/png';
        }
        else if (/\.svg$/i.test(imgUrl)) {
            mimeType = 'image/svg+xml';
        }
        img.src = `data:${mimeType};base64,${uint8ArrayToBase64(new Uint8Array(buffer))}`;
    }
    catch (err) {
        console.error('Failed loading image data');
    }
};
const isCanvasTainted = (canvas) => {
    try {
        canvas.getContext('2d')?.getImageData(0, 0, 1, 1);
        return false;
    }
    catch (err) {
        // TODO remove code and use name
        // name === 'SecurityError' || name === 'SECURITY_ERR'
        const code = err instanceof DOMException ? err.code : undefined;
        return code === 18;
    }
};
const mapToImageDataURL = (map, format = 'image/png', onTainted) => new Promise((resolve) => {
    map.once('rendercomplete', () => {
        const mapCanvas = document.createElement('canvas');
        const canvases = map.getTarget().querySelectorAll('canvas');
        let width = 0;
        let height = 0;
        canvases.forEach((canvas) => {
            width = canvas.width > width ? canvas.width : width;
            height = canvas.height > height ? canvas.height : height;
        });
        mapCanvas.width = width;
        mapCanvas.height = height;
        const mapContext = mapCanvas.getContext('2d');
        mapContext.fillStyle = '#fff';
        mapContext.fillRect(0, 0, width, height);
        Array.prototype.forEach.call(map.getTarget().querySelectorAll('canvas'), (canvas) => {
            if (canvas.width > 0) {
                const opacity = canvas.parentNode.style.opacity;
                mapContext.globalAlpha = opacity === '' ? 1 : Number(opacity);
                if (canvas.width !== width && canvas.height !== height) {
                    const transform = canvas.style.transform;
                    // Get the transform parameters from the style's transform matrix
                    const matrix = transform
                        .match(/^matrix\(([^(]*)\)$/)[1]
                        .split(',')
                        .map(Number);
                    // Apply the transform to the export map context
                    CanvasRenderingContext2D.prototype.setTransform.apply(mapContext, matrix);
                }
                if (isCanvasTainted(canvas)) {
                    console.error('[repex] tainted canvas');
                    onTainted?.();
                }
                else {
                    mapContext.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, width, height);
                }
            }
        });
        resolve(mapCanvas.toDataURL(format));
    });
    map.renderSync();
});
/**
 * Prevede libovolne HTML do PDF.
 *
 * Vezme originalni HTML a serialuzje vsechny CSS, IMG a CANVAS
 * a nasledne toto HTML odesle na serverovou sluzbu pres ajax, ktera vraci PDF,
 * to se nabidne k ulozeni pres FileSaver.
 *
 * @param formatConf definice sluzby z konfigurace
 * @param services sluzby pro generovani PDF/PNG
 * @param domNode HTML element, jehoz obsah se ma prevest na PDF
 * @param afterHtmlSerialize callback vyvolany po serializaci HTML
 * @return Promise
 */
export const htmlToPdf = async ({ map, format, fileName: fileNameOpt, domNode, afterHtmlSerialize, }) => {
    const fileName = fileNameOpt || 'export';
    const exportService = format.service;
    let clonedNode;
    let tainted = false;
    const onTainted = () => {
        tainted = true;
    };
    if (map) {
        const mapDataUrl = await mapToImageDataURL(map, 'image/jpeg', onTainted);
        const mapTarget = map.getTarget();
        let mapContainerId = mapTarget.getAttribute('id');
        if (!mapContainerId) {
            mapContainerId = 'map-container';
            mapTarget.setAttribute('id', mapContainerId);
        }
        clonedNode = domNode.cloneNode(true);
        const images = clonedNode.querySelectorAll('img');
        await Promise.all([...images].map(imgToBase64));
        const mapContainer = clonedNode.querySelector(`#${mapContainerId}`);
        mapContainer.innerHTML = '';
        const mapImage = document.createElement('image');
        mapImage.setAttribute('src', mapDataUrl);
        mapContainer.appendChild(mapImage);
    }
    else {
        clonedNode = domNode.cloneNode(true);
        const images = clonedNode.querySelectorAll('img');
        await Promise.all([...images].map(imgToBase64));
    }
    // get head & html code of the page
    let head = document
        ?.getElementsByTagName('head')
        ?.item(0)
        ?.outerHTML.replace(/<script [^>]+>?(<\/script>|\/>)/g, '');
    let html = clonedNode.outerHTML;
    html.replace(/<script [^>]+>?(<\/script>|\/>)/g, '');
    afterHtmlSerialize && afterHtmlSerialize();
    // add styles to head
    for (const link of document.head.querySelectorAll('link[rel="stylesheet"]')) {
        const linkres = await fetch(link.href, { credentials: 'same-origin' }); // zabezpeci posilani cookies (Edge)
        const style = await linkres.text();
        head = head?.replace(link.outerHTML, '');
        head = head?.replace('</head>', `<style>${style}</style>\n</head>`);
    }
    // relative => absolute path
    html = html?.replace(/href\s*=\s*"([^"]+\s*)"/gi, toAbsoluteHref);
    html = `<!DOCTYPE html><html>\n${head}\n<body>${html}</body>\n</html>`;
    if (tainted &&
        !window.confirm('Některé vrstvy nelze exportovat.\nChcete pokračovat v exportu neúplné mapy?')) {
        return true;
    }
    try {
        const gzipped = gzip(html);
        const headers = {
            'Content-Type': 'text/html',
            'Content-Encoding': 'gzip',
        };
        const response = await fetch(exportService, {
            method: 'POST',
            headers,
            body: gzipped,
        });
        console.info(`Export HTML size: ${html.length} (${gzipped.length} gzipped, ${Math.round((gzipped.length / html.length) * 100)} %)`);
        if (response.status !== 200)
            throw response.statusText;
        const data = await response.blob();
        FileSaver.saveAs(data, `${fileName}.${getFileType(response.headers.get('content-type'))}`);
    }
    catch (e) {
        console.error(e);
        return false;
    }
    return true;
};
/**
 * Prevede jakekoliv URL vzdy na absolutni, pouzito pro string.replace()
 * @param m regex match
 * @param url url string
 * @returns href string
 */
const toAbsoluteHref = (m, url) => {
    anchor.href = url;
    return `href="${anchor.href}"`;
};
//# sourceMappingURL=htmltopdf.js.map